import { useId } from 'react';

/** The little red note under an input. Rendered only when there is something to say. */
export function FieldError({ id, message }) {
  // A whitespace-only message means "paint this field invalid, but the sentence
  // is being shown somewhere else" — as login does for email + password.
  if (!message || !message.trim()) return null;
  return (
    <div className="field-error" id={id} role="alert">
      {message}
    </div>
  );
}

function Marker({ required }) {
  return required ? <span className="req" aria-hidden="true"> *</span> : null;
}

/** Generic wrapper: renders a label plus whatever control you pass as children. */
export default function Field({ label, full, hint, error, required, htmlFor, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={htmlFor}>
        {label}
        <Marker required={required} />
      </label>
      {children}
      <FieldError message={error} />
      {hint && !error && <div className="hint">{hint}</div>}
    </div>
  );
}

export function TextField({ label, full, hint, error, required, type = 'text', id: idProp, ...inputProps }) {
  const autoId = useId();
  const id = idProp || autoId;
  const errorId = `${id}-error`;
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {label}
        <Marker required={required} />
      </label>
      <input
        id={id}
        type={type}
        className={error ? 'invalid' : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        {...inputProps}
      />
      <FieldError id={errorId} message={error} />
      {hint && !error && <div className="hint">{hint}</div>}
    </div>
  );
}

export function SelectField({ label, full, hint, error, required, children, id: idProp, ...selectProps }) {
  const autoId = useId();
  const id = idProp || autoId;
  const errorId = `${id}-error`;
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={id}>
        {label}
        <Marker required={required} />
      </label>
      <select
        id={id}
        className={error ? 'invalid' : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        {...selectProps}
      >
        {children}
      </select>
      <FieldError id={errorId} message={error} />
      {hint && !error && <div className="hint">{hint}</div>}
    </div>
  );
}

/** Options for a catalog dropdown, with a leading placeholder. */
export function Options({ items, placeholder }) {
  return (
    <>
      <option value="">{placeholder}</option>
      {items.map((item) => (
        <option key={item} value={item}>
          {item}
        </option>
      ))}
    </>
  );
}

/**
 * One cell of an entry table. Wraps the control so an invalid value gets a red
 * border and the message shows on hover — a full error line under every cell
 * would blow the table's layout apart.
 */
export function Cell({ error, children }) {
  return (
    <td className={error ? 'cell-invalid' : undefined} title={error || undefined}>
      {children}
      {error && <span className="cell-error">{error}</span>}
    </td>
  );
}
